import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useFolderListContext } from "src/core/contexts/FolderListContext";
import {
  folderListFilterSchema,
  FolderListFilterValues,
} from "src/core/forms/folder/folderListFilter.schema";

const NONE_VALUE = "none";
const STORAGE_KEY = "folderListFilterValues";
const LABELS_STORAGE_KEY = "folderListFilterLabels";

type FolderListFilterLabels = {
  expertComptable: string;
  collaborator: string;
};

const EMPTY_LABELS: FolderListFilterLabels = {
  expertComptable: "",
  collaborator: "",
};

const getStoredValues = (): FolderListFilterValues | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setStoredValues = (values: FolderListFilterValues) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Ignore storage errors
  }
};

const getStoredLabels = (): FolderListFilterLabels => {
  if (typeof window === "undefined") return EMPTY_LABELS;
  try {
    const stored = localStorage.getItem(LABELS_STORAGE_KEY);
    if (!stored) return EMPTY_LABELS;
    const parsed = JSON.parse(stored) as Partial<FolderListFilterLabels>;
    return {
      expertComptable: typeof parsed.expertComptable === "string" ? parsed.expertComptable : "",
      collaborator: typeof parsed.collaborator === "string" ? parsed.collaborator : "",
    };
  } catch {
    return EMPTY_LABELS;
  }
};

const setStoredLabels = (labels: FolderListFilterLabels) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(labels));
  } catch {
    // Ignore storage errors
  }
};

export const useFolderListFilter = () => {
  const {
    ownerUserIdFilter,
    followedByUserIdFilter,
    setOwnerUserIdFilter,
    setFollowedByUserIdFilter,
    myFoldersFilter,
    setMyFoldersFilter,
  } = useFolderListContext();

  // Charger les valeurs depuis le localStorage ou utiliser les valeurs du contexte
  const storedValues = getStoredValues();
  const initialValues: FolderListFilterValues = storedValues || {
    myFolders: myFoldersFilter,
    expertComptable: ownerUserIdFilter || "",
    collaborator: followedByUserIdFilter || "",
  };

  const form = useForm<FolderListFilterValues>({
    resolver: zodResolver(folderListFilterSchema),
    defaultValues: initialValues,
  });

  const [labels, setLabels] = useState<FolderListFilterLabels>(getStoredLabels);

  const expertComptable = form.watch("expertComptable");
  const collaborator = form.watch("collaborator");
  const myFolders = form.watch("myFolders");
  const isMyFoldersDisabled = Boolean(
    (expertComptable && expertComptable !== NONE_VALUE) ||
      (collaborator && collaborator !== NONE_VALUE)
  );
  const areSelectsDisabled = !!myFolders;

  // Sauvegarder les valeurs dans le localStorage quand elles changent
  useEffect(() => {
    setStoredValues({
      myFolders: myFolders || false,
      expertComptable: expertComptable || "",
      collaborator: collaborator || "",
    });
  }, [myFolders, expertComptable, collaborator]);

  useEffect(() => {
    const value = expertComptable === NONE_VALUE || !expertComptable ? "" : expertComptable;
    if (value !== ownerUserIdFilter) {
      setOwnerUserIdFilter(value);
    }
  }, [expertComptable, setOwnerUserIdFilter, ownerUserIdFilter]);

  useEffect(() => {
    const value = collaborator === NONE_VALUE || !collaborator ? "" : collaborator;
    if (value !== followedByUserIdFilter) {
      setFollowedByUserIdFilter(value);
    }
  }, [collaborator, setFollowedByUserIdFilter, followedByUserIdFilter]);

  useEffect(() => {
    setLabels((prev) => {
      const next: FolderListFilterLabels = {
        expertComptable:
          expertComptable && expertComptable !== NONE_VALUE ? prev.expertComptable : "",
        collaborator: collaborator && collaborator !== NONE_VALUE ? prev.collaborator : "",
      };
      if (
        next.expertComptable === prev.expertComptable &&
        next.collaborator === prev.collaborator
      ) {
        return prev;
      }
      setStoredLabels(next);
      return next;
    });
  }, [expertComptable, collaborator]);

  const rememberLabel = (field: keyof FolderListFilterLabels, label: string) => {
    setLabels((prev) => {
      const next = { ...prev, [field]: label };
      setStoredLabels(next);
      return next;
    });
  };

  useEffect(() => {
    if (myFolders !== undefined && myFolders !== myFoldersFilter) {
      setMyFoldersFilter(myFolders as boolean);
    }
  }, [myFolders, setMyFoldersFilter, myFoldersFilter]);

  return {
    form,
    labels,
    rememberLabel,
    isMyFoldersDisabled,
    areSelectsDisabled,
  };
};
